
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Sparkles, Loader2 } from "lucide-react"

interface AIDietPlanGeneratorProps {
  title: string
  description: string
  placeholder: string
  examples?: string[]
  onGenerate: (prompt: string) => Promise<void>
  isGenerating?: boolean
}

export function AIDietPlanGenerator({
  title,
  description,
  placeholder,
  onGenerate,
  isGenerating = false
}: AIDietPlanGeneratorProps) {
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState("")

  const handleGenerate = async () => {
    if (!prompt.trim()) return

    try {
      await onGenerate(prompt)
      setPrompt("")
    } catch (error) {
      // Error handling is done in the parent component
      console.error("Error in AI generation:", error)
    }
  }

  const handleExampleClick = (example: string) => {
    setPrompt(example)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Textarea
          placeholder={placeholder}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          className="resize-none"
        />
      </CardContent>
      <CardFooter>
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || !prompt.trim()}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("ai.generating")}
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              {t("ai.generate")}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  )
}
